import { addDays, compareLocalDate, parseLocalDate } from '../recurrence/localDate.js';
import { weekdayShortNl } from '../time/nl.js';
// Islamitische feestdagen per traditie (golf 2, oprichter #1; onderzoek in
// docs/onderzoek/islamitische-kalendertradities.md). Een gezin kiest welke lijn het volgt:
//
// - `diyanet`: "Turks (Diyanet, vast rooster)". De data staan jaren vooruit vast (officiële
//   tabellen van Diyanet İşleri Başkanlığı). Nooit "(verwacht)".
// - `saudi`: "Saoedi-Arabië (maanzicht)". Vooraf alleen een verwachte dag (Umm al-Qura); de
//   aankondiging komt de avond ervoor. Tot dan "vr 20 of za 21 mrt (verwacht)".
// - `morocco`: "Marokko (maanzicht, bevestiging de avond ervoor)". Zelfde vorm; valt vaak een dag
//   later dan Saoedi-Arabië, daarom ook hier de keuze tussen de verwachte dag en de dag erna.
// - `own_mosque`: "Eigen moskee (±1 dag)". Het vaste rooster als startpunt, "(verwacht)", en een
//   ouder zet de dag een dag eerder of later als de moskee iets anders kiest.
//
// Een ouder kan bij maanzicht en eigen moskee een dag BEVESTIGEN of een dag verschuiven
// (household_profile.day_fixes, 0030_snel): sleutel "<tabel>:<verwachte datum>", waarde
// -1, 0 of +1. Een bevestigde dag verliest "(verwacht)". Bij Diyanet tellen verschuivingen niet.
//
// Bronnen (NOOIT de Aladhan-API: die geeft voor Diyanet 18 februari 2026, Diyanet zelf 19 februari):
// - DIYANET_TABLES: vakithesaplama.diyanet.gov.tr "Dini Günler" per jaar (icerik.php?icerik=153
//   voor 2026, 154 voor 2027, 185 t/m 192 voor 2028-2035), geraadpleegd 2026-09-25. Kolommen:
//   Ramazan Başlangıcı, Ramazan Bayramı 1. gün, Kurban Bayramı 1. gün, Hicri Yılbaşı en Mevlid
//   Kandili (de avond vóór 12 Rebiülevvel, zoals Turks-Nederlandse gezinnen hem vieren).
// - UMM_AL_QURA_TABLES: de officiële Umm al-Qura-kalender van Saoedi-Arabië (1 Ramadan, 1 Shawwal,
//   10 Dhu al-Hijja, 1 Muharram, 12 Rabi' al-Awwal), gegenereerd met
//   packages/domain/scripts/umm-al-qura.mjs uit de Umm al-Qura-tabel in ICU (Intl-kalender
//   `islamic-umalqura`); 2026 gecontroleerd tegen SPA (Ramadan 18 februari, Eid al-Fitr 20 maart).
//   islamic.test.ts vergelijkt de tabel met ICU, zodat een tikfout opvalt.
//
// Puur en ES2018 (de kiosk typecheckt de profielbestanden): geen flatMap, geen Object.fromEntries.
export const ISLAMIC_TRADITIONS = ['diyanet', 'saudi', 'morocco', 'own_mosque'];
export function isIslamicTradition(v) {
    return typeof v === 'string' && ISLAMIC_TRADITIONS.indexOf(v) >= 0;
}
export const ISLAMIC_TABLE_KEYS = [
    'ramadan_start',
    'eid_al_fitr',
    'eid_al_adha',
    'islamic_new_year',
    'mawlid',
];
export function isIslamicTable(v) {
    return ISLAMIC_TABLE_KEYS.indexOf(v) >= 0;
}
/** Jaren waarvoor beide tabellen volledig zijn (islamic.test.ts bewaakt dat). */
export const ISLAMIC_TABLE_YEARS = { from: 2026, to: 2035 };
/** Officiële Diyanet-tabellen ('MM-DD'; twee keer in één jaar = lijst). Bron: zie boven. */
export const DIYANET_TABLES = {
    ramadan_start: {
        2026: '02-19',
        2027: '02-08',
        2028: '01-28',
        2029: '01-16',
        2030: ['01-05', '12-26'],
        2031: '12-15',
        2032: '12-04',
        2033: '11-23',
        2034: '11-12',
        2035: '11-01',
    },
    eid_al_fitr: {
        2026: '03-20',
        2027: '03-09',
        2028: '02-26',
        2029: '02-14',
        2030: '02-04',
        2031: '01-24',
        2032: '01-14',
        2033: ['01-02', '12-23'],
        2034: '12-12',
        2035: '12-01',
    },
    eid_al_adha: {
        2026: '05-27',
        2027: '05-16',
        2028: '05-05',
        2029: '04-24',
        2030: '04-13',
        2031: '04-02',
        2032: '03-22',
        2033: '03-11',
        2034: '03-01',
        2035: '02-18',
    },
    islamic_new_year: {
        2026: '06-16',
        2027: '06-06',
        2028: '05-25',
        2029: '05-14',
        2030: '05-03',
        2031: '04-23',
        2032: '04-11',
        2033: '04-01',
        2034: '03-21',
        2035: '03-11',
    },
    mawlid: {
        2026: '08-24',
        2027: '08-13',
        2028: '08-02',
        2029: '07-23',
        2030: '07-12',
        2031: '07-01',
        2032: '06-19',
        2033: '06-09',
        2034: '05-29',
        2035: '05-19',
    },
};
/** Umm al-Qura (verwachte dag bij maanzicht). Mawlid = 12 Rabi' al-Awwal zelf. Bron: zie boven. */
export const UMM_AL_QURA_TABLES = {
    ramadan_start: {
        2026: '02-18',
        2027: '02-08',
        2028: '01-28',
        2029: '01-16',
        2030: ['01-05', '12-26'],
        2031: '12-16',
        2032: '12-04',
        2033: '11-23',
        2034: '11-12',
        2035: '11-01',
    },
    eid_al_fitr: {
        2026: '03-20',
        2027: '03-09',
        2028: '02-26',
        2029: '02-14',
        2030: '02-04',
        2031: '01-24',
        2032: '01-14',
        2033: ['01-03', '12-23'],
        2034: '12-12',
        2035: '12-01',
    },
    eid_al_adha: {
        2026: '05-27',
        2027: '05-16',
        2028: '05-05',
        2029: '04-24',
        2030: '04-13',
        2031: '04-02',
        2032: '03-22',
        2033: '03-12',
        2034: '03-01',
        2035: '02-19',
    },
    islamic_new_year: {
        2026: '06-16',
        2027: '06-06',
        2028: '05-25',
        2029: '05-14',
        2030: '05-04',
        2031: '04-23',
        2032: '04-11',
        2033: '04-01',
        2034: '03-22',
        2035: '03-11',
    },
    mawlid: {
        2026: '08-25',
        2027: '08-14',
        2028: '08-03',
        2029: '07-24',
        2030: '07-13',
        2031: '07-02',
        2032: '06-20',
        2033: '06-10',
        2034: '05-30',
        2035: '05-20',
    },
};
/** Welke tabel een traditie volgt. */
export function islamicSource(tradition) {
    return tradition === 'saudi' || tradition === 'morocco' ? 'umm_al_qura' : 'diyanet';
}
/** Verwachte startdata ('MM-DD') van een tabel in een jaar voor een traditie (0, 1 of 2). */
export function islamicTableDates(tradition, table, year) {
    const src = islamicSource(tradition) === 'diyanet' ? DIYANET_TABLES : UMM_AL_QURA_TABLES;
    const v = src[table][year];
    if (v === undefined)
        return [];
    return typeof v === 'string' ? [v] : v.slice();
}
/**
 * Standaardtraditie als het gezin (nog) niets koos: het vaste rooster van Diyanet (een vaste datum
 * geeft geen verrassingen; bij de onboarding vragen we het toch, zodra de islamitische dagen
 * aangeboden worden, DESIGN §8.P.7). Review golf 6c ronde 1 (f2-14, "naar gewoontes, niet naar
 * afkomst"): NOOIT meer uit de keuken. Een signaal uit de gewoontes (de gebedskalender van Diyanet
 * of Milli Görüş) zou hetzelfde opleveren, dus lezen we die niet. Bestaande gezinnen die via de
 * keuken op Marokko of Saoedi-Arabië stonden, houden die kalender: 0045_mazhab stap 4 legde hem
 * vast in day_calendar.
 */
export function defaultIslamicTradition() {
    return 'diyanet';
}
/** Toont deze traditie "(verwacht)" tot een ouder bevestigt? */
export function islamicExpected(tradition) {
    return tradition !== 'diyanet';
}
/** Toont deze traditie de keuze "vr 20 of za 21 mrt" (maanzicht)? */
export function islamicEitherOr(tradition) {
    return tradition === 'saudi' || tradition === 'morocco';
}
/** Bevestigen of verschuiven kan (niet bij het vaste rooster). */
export function islamicFixable(tradition) {
    return tradition !== 'diyanet';
}
/** Hoeveel een ouder mag verschuiven: maanzicht alleen de verwachte dag of de dag erna. */
export function islamicShiftOptions(tradition) {
    if (tradition === 'diyanet')
        return [];
    if (tradition === 'own_mosque')
        return [-1, 0, 1];
    return [0, 1];
}
/** Maximaal aantal bevestigingen dat we bewaren (0030: jsonb met hooguit zoveel sleutels). */
export const ISLAMIC_FIXES_MAX = 60;
/** Vorm van een sleutel: "<tabel>:<verwachte datum>". */
export const ISLAMIC_FIX_KEY_PATTERN = /^(ramadan_start|eid_al_fitr|eid_al_adha|islamic_new_year|mawlid):\d{4}-\d{2}-\d{2}$/;
export function islamicFixKey(table, expectedDate) {
    return `${table}:${expectedDate}`;
}
/**
 * Canonieke bevestigingen: alleen geldige sleutels met een toegestane waarde voor deze traditie,
 * gesorteerd, en hooguit ISLAMIC_FIXES_MAX (de nieuwste blijven). Bij Diyanet niets
 * (minimalisatie: een vast rooster heeft geen bevestiging nodig).
 */
export function canonicalIslamicFixes(fixes, tradition) {
    const out = {};
    if (!fixes || typeof fixes !== 'object')
        return out;
    const allowed = islamicShiftOptions(tradition);
    const keys = Object.keys(fixes)
        .filter((k) => ISLAMIC_FIX_KEY_PATTERN.test(k))
        .filter((k) => {
        const v = fixes[k];
        return typeof v === 'number' && allowed.indexOf(v) >= 0;
    })
        .sort((a, b) => {
        const da = a.slice(a.indexOf(':') + 1);
        const db = b.slice(b.indexOf(':') + 1);
        return da < db ? -1 : da > db ? 1 : a < b ? -1 : 1;
    });
    const kept = keys.slice(Math.max(0, keys.length - ISLAMIC_FIXES_MAX));
    for (const k of kept)
        out[k] = fixes[k];
    return out;
}
/**
 * Een geldige sleutel met een waarde die deze traditie niet toestaat (bv. -1 bij maanzicht): de
 * API geeft dan 400 invalid_day_fix in plaats van de keuze stil weg te laten. Sleutels van een
 * andere vorm vangt het schema al.
 */
export function hasDisallowedIslamicFix(fixes, tradition) {
    if (!fixes || typeof fixes !== 'object')
        return false;
    const allowed = islamicShiftOptions(tradition);
    return Object.keys(fixes).some((k) => {
        const v = fixes[k];
        return ISLAMIC_FIX_KEY_PATTERN.test(k) && typeof v === 'number' && allowed.indexOf(v) < 0;
    });
}
/**
 * Bevestigde data uit het onderzoek (docs/onderzoek/islamitische-kalendertradities.md §2): de
 * verschuiving t.o.v. de verwachte dag. Alleen gebruikt voor een dag die al voorbij is en die het
 * gezin zelf niet bevestigde. Marokko begon de Ramadan 2026 een dag later (do 19 feb).
 */
export const KNOWN_SIGHTINGS = {
    saudi: { 'ramadan_start:2026-02-18': 0, 'eid_al_fitr:2026-03-20': 0 },
    morocco: { 'ramadan_start:2026-02-18': 1, 'eid_al_fitr:2026-03-20': 0 },
};
/** Alle startdata van een islamitische tabel in `year` voor een gezin. */
export function islamicDates(table, year, choice) {
    const y = String(year).padStart(4, '0');
    const t = choice.tradition;
    const out = [];
    for (const md of islamicTableDates(t, table, year)) {
        const expectedDate = `${y}-${md}`;
        const fixKey = islamicFixKey(table, expectedDate);
        const fix = islamicFixable(t) ? choice.fixes[fixKey] : undefined;
        const confirmed = typeof fix === 'number' && islamicShiftOptions(t).indexOf(fix) >= 0;
        // Voorbij: ook de laatst mogelijke dag (de dag erna) ligt vóór vandaag.
        const past = choice.settledBefore !== undefined &&
            compareLocalDate(addDays(expectedDate, 1), choice.settledBefore) < 0;
        const known = t === 'saudi' || t === 'morocco' ? KNOWN_SIGHTINGS[t][fixKey] : undefined;
        const shift = confirmed ? fix : past && known !== undefined ? known : 0;
        const open = !confirmed && !past;
        out.push({
            date: shift !== 0 ? addDays(expectedDate, shift) : expectedDate,
            expectedDate,
            fixKey,
            expected: islamicExpected(t) && open,
            confirmed,
            alternative: islamicEitherOr(t) && open ? addDays(expectedDate, 1) : null,
        });
    }
    return out;
}
const MONTH_SHORT_NL = [
    'jan',
    'feb',
    'mrt',
    'apr',
    'mei',
    'jun',
    'jul',
    'aug',
    'sep',
    'okt',
    'nov',
    'dec',
];
/** "vr 20 mrt" (Nederlands, voor de voorgeformatteerde dagen van de kiosk en de agenda). */
export function shortDateNl(date) {
    const { month, day } = parseLocalDate(date);
    return `${weekdayShortNl(date)} ${day} ${MONTH_SHORT_NL[month - 1] ?? ''}`;
}
/**
 * "vr 20 of za 21 mrt" (zelfde maand) of "di 31 mrt of wo 1 apr". Is er geen tweede dag, dan
 * alleen de eerste.
 */
export function eitherOrNl(date, alternative) {
    if (!alternative || compareLocalDate(date, alternative) === 0)
        return shortDateNl(date);
    const a = parseLocalDate(date);
    const b = parseLocalDate(alternative);
    if (a.month === b.month) {
        return `${weekdayShortNl(date)} ${a.day} of ${shortDateNl(alternative)}`;
    }
    return `${shortDateNl(date)} of ${shortDateNl(alternative)}`;
}
//# sourceMappingURL=islamic.js.map